import React, { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';

import { dismissSubtypeAdjustedFlag } from '../../editor-commands/subtype-adjusted-flag';
import type { MalleableUiPlugin, MalleableUiProductFlags } from '../../malleableUiPluginType';
import { messages } from '../messages';

type Props = {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	productFlags?: MalleableUiProductFlags;
};

const FLAG_ID = 'maui-remix-subtype-adjusted';

/**
 * Renders the "Output adjusted" flag (backend produced a different type than
 * selected). Driven by durable plugin state so it survives the remix content
 * component tearing down on completion. Auto-dismisses after 8 seconds (or on
 * manual dismiss); dismissal clears the durable plugin state.
 */
export const SubtypeAdjustedFlag = ({ api, productFlags }: Props): React.JSX.Element | null => {
	const intl = useIntl();
	const isShown = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.showSubtypeAdjustedFlag ?? false,
	);

	const onDismissed = useCallback(() => {
		api?.core.actions.execute(dismissSubtypeAdjustedFlag());
	}, [api]);

	const title = intl.formatMessage(messages.subtypeAdjustedFlagTitle);
	const description = intl.formatMessage(messages.subtypeAdjustedFlagDescription);

	// Track whether we have ever raised the flag through the product sink, so we
	// only call hideFlag after a real show. Without this guard, hideFlag(FLAG_ID)
	// would fire on initial mount whenever productFlags is provided and isShown is
	// false, spuriously asking the host to hide a flag that was never shown.
	const hasShownFlag = useRef(false);

	useEffect(() => {
		if (!productFlags) {
			return;
		}

		if (!isShown) {
			if (hasShownFlag.current) {
				productFlags.hideFlag(FLAG_ID);
				hasShownFlag.current = false;
			}
			return;
		}

		hasShownFlag.current = true;
		productFlags.showFlag({
			autoDismissSeconds: 8,
			description,
			icon: <StatusInformationIcon label="" color={token('color.icon.information')} />,
			id: FLAG_ID,
			onDismissed,
			title,
			type: 'info',
		});
	}, [description, isShown, onDismissed, productFlags, title]);

	if (productFlags) {
		return null;
	}

	if (!isShown) {
		return null;
	}

	return (
		<FlagGroup>
			<AutoDismissFlag
				onDismissed={onDismissed}
				title={title}
				description={description}
				id={FLAG_ID}
				testId={FLAG_ID}
				icon={<StatusInformationIcon label="" color={token('color.icon.information')} />}
			/>
		</FlagGroup>
	);
};
